import { useEffect, useState } from "react"
import { Button, Text } from "@chakra-ui/react"
import {
  ArrowRight,
  Block,
  BlockProps,
  Link,
  useStyletron,
} from "@mezo-org/mezo-clay"
import { AnimatePresence, motion } from "framer-motion"

import closeIcon from "#/assets/close-button-banner.svg"

type LandingTopBannerProps = BlockProps & {
  banner?: boolean
  closeButton?: boolean
  title?: string
  buttonText?: string
  buttonLink?: string
  bannerLevel?: "l1" | "l2" | "l3"
}

export default function LandingTopBanner({
  banner,
  closeButton,
  title,
  buttonText,
  buttonLink,
  bannerLevel,
  ...props
}: LandingTopBannerProps) {
  const [css, theme] = useStyletron()
  const [visible, setVisible] = useState(false)
  const isEnabled = banner ?? true
  const showCloseButton = closeButton ?? true
  const headingText = title ?? ""
  const ctaText = buttonText ?? ""
  const ctaLink = buttonLink ?? ""

  const backgroundMap = {
    l1: "black",
    l2: "linear-gradient(90deg, #6B0036 0%, #D10069 33.65%, #FF004D 68.75%, #6B0036 100%)",
    l3: "#DE1135",
  } as const

  const level = (
    ["l1", "l2", "l3"].includes(bannerLevel as string) ? bannerLevel : "l1"
  ) as keyof typeof backgroundMap

  const background = backgroundMap[level]

  useEffect(() => {
    const isDismissed = localStorage.getItem("top_banner_dismissed")
    if (!isDismissed) {
      setVisible(true)
    }
  }, [])

  useEffect(() => {
    if (isEnabled === false) {
      setVisible(false)
    }
  }, [isEnabled])

  const handleClose = () => {
    setVisible(false)
    localStorage.setItem("top_banner_dismissed", "true")
  }

  const bannerHeight = 50 // adjust to match your paddingTop + paddingBottom + font height etc.

  if (banner === false) {
    return null
  }

  return (
    <>
      {visible && <div style={{ height: `${bannerHeight}px` }} />}
      <AnimatePresence>
        {visible && (
          <motion.div
            initial={{ opacity: 1, y: 0, position: "fixed", zIndex: "999" }}
            exit={{ y: -10 }}
          >
            <Block
              position="fixed"
              top={0}
              left={0}
              width="100%"
              paddingTop={theme.sizing.scale500}
              paddingBottom={theme.sizing.scale500}
              className={`top-banner ${visible ? "visible" : ""}`}
              overrides={{
                Block: {
                  style: {
                    zIndex: 9999,
                    textAlign: "center",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: theme.sizing.scale400,
                    position: "fixed",
                    color: "white",
                    background,
                  },
                },
              }}
              {...props}
            >
              <Text
                fontSize={{ base: "12px", md: "15px" }}
                dangerouslySetInnerHTML={{ __html: headingText }}
              />
              {ctaText && (
                <Link href={ctaLink}>
                  <Button
                    background="white"
                    padding="5px 10px"
                    fontSize={{ base: "12px", md: "14px" }}
                    fontWeight="normal"
                    borderRadius="5px"
                    gap="6px"
                    onClick={handleClose}
                  >
                    {ctaText}
                    <ArrowRight size={theme.sizing.scale600} />
                  </Button>
                </Link>
              )}
              {showCloseButton && (
                <div
                  className={css({
                    position: "absolute",
                    right: theme.sizing.scale600,
                  })}
                >
                  <Button
                    onClick={handleClose}
                    bg="transparent"
                    _hover={{ bg: "transparent", opacity: "0.5" }}
                    marginRight={{ base: "-10px", md: 0 }}
                  >
                    <img src={closeIcon} alt="close button" />
                  </Button>
                </div>
              )}
            </Block>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
